import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Button, Container, Typography, MenuItem, Select,
  FormControl, InputLabel, Stack, CircularProgress, useMediaQuery, Pagination
} from '@mui/material';
import { red } from '@mui/material/colors';

import useResponsive from '../hooks/useResponsive';
import { getallproperties, searchProperties, getallcities } from '../services/apiService';
import MoreFilters from '../common/MoreFilters';
import location from '../assets/location.png';
import noProperty from '../assets/noProperty.png';
import eye from '../assets/eye.png';
const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};
const ResidentialsList = () => {
  const isTablet = useMediaQuery('(min-width:700px) and (max-width:1100px)');
  const { isSmallScreen } = useResponsive();
  const navigate = useNavigate();
  const [locationOptions, setLocationOptions] = useState([]);
  const locationState = useLocation();
  const searchParams = new URLSearchParams(locationState.search);
  const initialLocation = searchParams.get('location') || locationState.state?.selectedLocation || locationState.state?.location || '';
  const searchValue = searchParams.get('search') || locationState.state?.searchQuery || '';
  const [residentialData, setResidentialData] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0
  });
  const [, setLoading] = useState(true);
  const [cardLoading, setCardLoading] = useState(false);

  const buildParams = useCallback((filters, isFiltering = false) => {
    const params = {};

    // Note: Search filtering is now done client-side, so we don't send search parameters to the API
    // This allows us to search across multiple fields (name, address, city, state, etc.)

    // Handle location filter
    if (filters.location && filters.location !== 'all' && filters.location !== 'All') {
      params.location = filters.location;
    }

    if (filters.bedrooms) {
      const match = String(filters.bedrooms).match(/(\d+)/);
      if (match) params.bhkType = match[1];
    }
    if (filters.budget) {
      if (filters.budget === '<50L') {
        params.maxPrice = 5000000;
      } else if (filters.budget === '50L-1Cr') {
        params.minPrice = 5000000;
        params.maxPrice = 10000000;
      }
    }
    if (filters.possession) params.possessionDate = filters.possession;

    // Handle 'All' locations
    if (!filters.location || filters.location === 'all' || filters.location === 'All') {
      // No need to add location parameter, it will be handled by API
    }

    // Also check moreLocations for additional location filters
    if (filters.moreLocations && filters.moreLocations.length > 0) {
      const validLocations = filters.moreLocations.filter(loc => loc.toLowerCase() !== 'all');
      if (validLocations.length > 0) {
        // If we already have a location from the main dropdown, combine them
        if (params.location) {
          params.location = [params.location, ...validLocations].join(',');
        } else {
          params.location = validLocations.join(',');
        }
      }
    }

    const statusMap = {
      'Ready to Move In': 'Ready to Move',
    };
    if (filters.projectStatus && filters.projectStatus.length > 0) {
      params.status = filters.projectStatus.map(s => statusMap[s] || s).join(',');
    }
    if (filters.propertyType?.length) params.propertyType = filters.propertyType.join(',');
    if (filters.bathrooms?.length) params.bathrooms = filters.bathrooms.join(',');
    if (filters.area?.length) {
      const areaRanges = filters.area.filter(a => a !== 'more').map(a => a.split('-').map(Number));
      if (areaRanges.length > 0) {
        params.minArea = Math.min(...areaRanges.map(r => r[0]));
        params.maxArea = Math.max(...areaRanges.map(r => r[1]));
      }
      if (filters.area.includes('more')) {
        params.minArea = 3250;
      }
    }
    if (filters.availability?.length) params.availability = filters.availability.join(',');

    // Always add pagination parameters
    params.page = filters.page || 1;
    params.limit = 10; // consistent page size

    return params;
  }, [searchValue]);

  const [filters, setFilters] = useState({
    bedrooms: '',
    budget: '',
    possession: '',
    location: initialLocation,
    projectStatus: [],
    propertyType: [],
    bathrooms: [],
    area: [],
    availability: [],
    moreLocations: [],
    page: 1, // Add page to filters
  });

  const initialLoad = useRef(true);

  // Handle search results from navbar navigation
  useEffect(() => {
    if (locationState.state?.searchResults) {
      const searchQuery = locationState.state.searchQuery || '';
      const selectedLocation = locationState.state.selectedLocation || '';
      let filteredProperties = locationState.state.searchResults.properties || [];

      // If there's a search query, filter the properties
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        filteredProperties = filteredProperties.filter(property => {
          // Search in property name
          if (property.name && property.name.toLowerCase().includes(query)) return true;

          // Search in group name
          if (property.groupName && property.groupName.toLowerCase().includes(query)) return true;

          // Search in locations (address, city, state)
          if (property.locations && property.locations.length > 0) {
            return property.locations.some(location => {
              if (location.address && location.address.toLowerCase().includes(query)) return true;
              if (location.city && location.city.toLowerCase().includes(query)) return true;
              if (location.state && location.state.toLowerCase().includes(query)) return true;
              return false;
            });
          }

          return false;
        });
      }

      // Update the location filter if selectedLocation is provided
      if (selectedLocation && selectedLocation !== filters.location) {
        setFilters(prev => ({
          ...prev,
          location: selectedLocation
        }));
      }

      setResidentialData(filteredProperties);
      setPagination({
        currentPage: locationState.state.searchResults.currentPage || 1,
        totalPages: locationState.state.searchResults.totalPages || 1,
        totalItems: filteredProperties.length
      });
      // Clear the state to prevent re-rendering on refresh
      navigate(locationState.pathname + locationState.search, { replace: true });
    }
  }, [locationState.state, navigate, filters.location]);

  // Fetch cities on component mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await getallcities();
        if (response?.status === true && Array.isArray(response.cities)) {
          setLocationOptions(response.cities);
        }
      } catch (error) {
        console.error('Failed to fetch cities:', error);
      }
    };

    fetchLocations();
  }, []);

  // Handle location changes from URL or state
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLocation = urlParams.get('location') || '';
    const stateLocation = locationState.state?.selectedLocation || locationState.state?.location || '';
    const newLocation = urlLocation || stateLocation;

    // Only update if the location has actually changed and it's not 'All'
    if (newLocation !== filters.location) {
      setFilters(prev => ({
        ...prev,
        location: newLocation === 'All' ? '' : newLocation
      }));

      // If we have a new location from URL that's different from state, update the state to match
      if (urlLocation && urlLocation !== stateLocation) {
        navigate(window.location.pathname, {
          replace: true,
          state: {
            ...locationState.state,
            location: urlLocation === 'All' ? '' : urlLocation
          }
        });
      }
    }
  }, [locationState.search, locationState.state, navigate]);

  // Fetch data when filters change
  useEffect(() => {
    const fetchData = async () => {
      if (initialLoad.current) {
        initialLoad.current = false;
        return;
      }

      setCardLoading(true);
      try {
        const params = buildParams(filters);
        const response = await getallproperties(params);
        let properties = response.properties || [];

        // Apply client-side search filtering if there's a search query
        if (searchValue && searchValue.trim()) {
          const query = searchValue.toLowerCase().trim();
          properties = properties.filter(property => {
            // Search in property name
            if (property.name && property.name.toLowerCase().includes(query)) return true;

            // Search in group name
            if (property.groupName && property.groupName.toLowerCase().includes(query)) return true;

            // Search in locations (address, city, state)
            if (property.locations && property.locations.length > 0) {
              return property.locations.some(location => {
                if (location.address && location.address.toLowerCase().includes(query)) return true;
                if (location.city && location.city.toLowerCase().includes(query)) return true;
                if (location.state && location.state.toLowerCase().includes(query)) return true;
                return false;
              });
            }

            return false;
          });
        }

        setResidentialData(properties);
        setPagination({
          currentPage: response.currentPage || 1,
          totalPages: response.totalPages || 1,
          totalItems: properties.length
        });
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setCardLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchValue, buildParams]);






  const handleFilterChange = (filterType, value, event) => {
    // Prevent default form submission behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setFilters(prev => {
      // Reset page to 1 when changing filters
      const newFilters = {
        ...prev,
        [filterType]: value === 'all' ? '' : value, // Convert 'all' to empty string for consistency
        page: 1 // Reset page to 1 when changing filters
      };

      // Update URL and state for location changes
      if (filterType === 'location') {
        const searchParams = new URLSearchParams(window.location.search);

        // Update URL
        if (value === 'all' || !value) {
          searchParams.delete('location');
        } else {
          searchParams.set('location', value);
        }
        window.history.replaceState({}, '', `?${searchParams.toString()}`);

        // Update state
        setFilters(prev => ({
          ...prev,
          location: value === 'all' ? '' : value
        }));
      }

      // The useEffect will handle the API call when filters change

      return newFilters;
    });
  };

  const resetFilters = () => {
    // Clear all filters
    const defaultFilters = {
      bedrooms: '',
      budget: '',
      possession: '',
      location: '',
      projectStatus: [],
      propertyType: [],
      bathrooms: [],
      area: [],
      availability: [],
      moreLocations: [],
      page: 1
    };

    setFilters(defaultFilters);

    // Clear search params from URL
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('search');
    searchParams.delete('location');

    // Navigate to clean URL
    navigate({
      pathname: window.location.pathname,
      search: searchParams.toString(),
      state: {}
    }, { replace: true });

    // Fetch data 
    const params = buildParams(defaultFilters, false);
    fetchFilteredData(params, true, 1);
  };



  const fetchFilteredData = useCallback(async (customParams = null, showLoader = true, page = filters.page) => {
    if (showLoader) {
      setCardLoading(true);
    }

    try {
      const params = customParams || buildParams(filters, page);
      const response = await getallproperties(params);

      // Log the API response for debugging

      // Check if response has a properties array
      if (response && response.properties && Array.isArray(response.properties)) {
        // Update the residential data with the properties array
        setResidentialData(response.properties);

        // Update pagination info from the response
        setPagination({
          currentPage: response.currentPage || page,
          totalPages: response.totalPages || 1,
          totalItems: response.totalItems || response.properties.length
        });
      } else if (Array.isArray(response)) {
        // If the response is directly an array (for backward compatibility)
        setResidentialData(response);
        setPagination(prev => ({
          ...prev,
          currentPage: page,
          totalItems: response.length
        }));
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      if (showLoader) {
        setTimeout(() => setCardLoading(false), 500);
      }
      setLoading(false);
    }
  }, [filters, buildParams]);

  useEffect(() => {
    if (!initialLoad.current) {
      fetchFilteredData(null, true, filters.page);
    }

    return () => {
      if (fetchFilteredData.cancel) {
        fetchFilteredData.cancel();
      }
    };
  }, [filters, fetchFilteredData]);

  const filteredData = Array.isArray(residentialData)
    ? searchValue
      ? residentialData.filter(project =>
        project?.name?.toLowerCase().includes(searchValue.toLowerCase())
      )
      : [...residentialData] // Create a new array to ensure reactivity
    : [];


  const formatPrice = (price) => {
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2).replace(/\.00$/, '')} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2).replace(/\.00$/, '')} L`;
    return `₹${price.toLocaleString()}`;
  };

  const handlePageChange = (event, page) => {
    // Update the page in filters
    setFilters(prev => ({
      ...prev,
      page
    }));

    // Trigger API call with pagination (no filtering)
    const params = buildParams({ ...filters, page }, false);
    fetchFilteredData(params, true, page);

    // Scroll to top when changing pages
    window.scrollTo(0, 0);
  };

  return (
    <Container sx={{ py: 4, mt: 9 }}>

      <Box sx={{ p: isSmallScreen ? 2 : 4 }}>
        <Stack
          direction={isSmallScreen || isTablet ? 'column' : 'row'}
          width="100%"
          spacing={2}
          mb={3}
          sx={{ px: { xs: 0, sm: 3 } }}
          alignItems={isSmallScreen ? 'stretch' : 'center'}
        >
          {/* Bedrooms */}
          <FormControl size="small" sx={{ minWidth: isSmallScreen || isTablet ? '100%' : 200 }}>
            <InputLabel>Bedrooms</InputLabel>
            <Select
              label="Bedrooms"
              value={filters.bedrooms}
              onChange={(e) => handleFilterChange('bedrooms', e.target.value)}
            >
              <MenuItem value=""><em>Any</em></MenuItem>
              <MenuItem value="1BHK">1BHK</MenuItem>
              <MenuItem value="2BHK">2BHK</MenuItem>
              <MenuItem value="3BHK">3BHK</MenuItem>
            </Select>
          </FormControl>

          {/* Budget */}
          <FormControl size="small" sx={{ minWidth: isSmallScreen || isTablet ? '100%' : 200 }}>
            <InputLabel>Budget</InputLabel>
            <Select
              label="Budget"
              value={filters.budget}
              onChange={(e) => handleFilterChange('budget', e.target.value)}
            >
              <MenuItem value=""><em>Any</em></MenuItem>
              <MenuItem value="<50L">Under ₹50L</MenuItem>
              <MenuItem value="50L-1Cr">₹50L - ₹1Cr</MenuItem>
            </Select>
          </FormControl>

          {/* Possession */}
          <FormControl size="small" sx={{ minWidth: isSmallScreen || isTablet ? '100%' : 200 }}>
            <InputLabel>Possession with</InputLabel>
            <Select
              label="Possession with"
              value={filters.possession}
              onChange={(e) => handleFilterChange('possession', e.target.value)}
            >
              <MenuItem value=""><em>Any</em></MenuItem>
              <MenuItem value="6months">6 Months</MenuItem>
              <MenuItem value="1yr">1 Year</MenuItem>
            </Select>
          </FormControl>

          {/* Location Dropdown with Grouped Cities */}
          <FormControl size="small" sx={{ minWidth: isSmallScreen || isTablet ? '100%' : 200 }}>
            <InputLabel>Location</InputLabel>
            <Select
              label="Location" value={filters.location || ''}
              onChange={(e) => handleFilterChange('location', e.target.value, e)}
            >
              <MenuItem value="all">Show All</MenuItem>
              {locationOptions.map((loc) => (
                <MenuItem key={loc} value={loc}>
                  {loc}
                </MenuItem>
              ))}
            </Select>
          </FormControl>



          {/* More Filters */}
          <MoreFilters filters={filters} handleFilterChange={handleFilterChange} locationOptions={locationOptions} />

          {/* Reset Button */}
          <Button
            startIcon={<i className="fas fa-rotate-left"></i>}
            variant="contained"
            color="error"
            sx={{
              height: 40,
              width: isSmallScreen ? '100%' : 'auto',
              ml: isSmallScreen ? 0 : 'auto',
            }}
            onClick={resetFilters}
          >
            Reset
          </Button>
        </Stack>

        {/* Heading */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', my: 2 }}>
          <Typography variant="h3" sx={{ fontSize: { xs: "17px", md: "24px" } }} fontWeight={600}>Best Offers & Deals on Residential Projects</Typography>
        </Box>

        {/* Residential Cards */}
        <Box sx={{ position: 'relative', mt: 2 }}>
          {cardLoading && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1,
              borderRadius: 2,
              pointerEvents: 'none'
            }}>
              <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main' }} />
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {cardLoading ? (
              ''
            ) : filteredData.length === 0 ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '300px',
                textAlign: 'center',
                p: 4
              }}>
                <img
                  src={noProperty} alt="No properties found"
                  style={{ width: '200px', marginBottom: '20px' }}
                />
                <Typography variant="h5" color="textSecondary" gutterBottom>
                  No properties found
                </Typography>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                  We couldn't find any properties matching your search criteria.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={resetFilters}
                  startIcon={<i className="fas fa-filter"></i>}
                >
                  Clear Filters
                </Button>
              </Box>
            ) : (
              filteredData.map((project) => (
                <Box key={project.id} sx={{
                  display: 'flex', flexDirection: isSmallScreen ? 'column' : 'row',
                  borderRadius: 3, mb: 3, border: '1px solid #eee', overflow: 'hidden',
                }}>
                  <Box
                    component="img"
                    src={project.proertyImage}
                    alt={project.name}
                    sx={{ width: isSmallScreen ? '100%' : 300, height: isSmallScreen ? 200 : 280, objectFit: 'cover' }}
                  />
                  <Box sx={{ flex: 1, p: isSmallScreen ? 2 : 3 }}>
                    <Typography variant="h6" fontWeight={600}>{project.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {project.locations?.[0] && <img src={location} alt="location" />}
                      <Typography variant="body1" sx={{ fontFamily: 'Lato, Arial, sans-serif', fontWeight: 400 }} color="text.secondary">
                        {[
                          project.locations?.[0]?.state,
                          project.locations?.[0]?.city,
                          project.locations?.[0]?.address || 'Address not available'
                        ].filter(Boolean).join(', ')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'inline-block', px: 1.5, py: 0.5, bgcolor: '#BB081B17', color: '#BB081B', borderRadius: '16px', mt: 1, mb: 2 }}>
                      Possession starts from: {project.possessionDate ? (() => {
                        const date = new Date(project.possessionDate);
                        return `${date.getDate()} ${date.toLocaleString('en-GB', { month: 'short' })} ${date.getFullYear().toString().slice(-2)}`;
                      })() : 'N/A'}
                    </Box>
                    <Box mb={2}>
                      {project.floorplans?.length > 0 ? (
                        Object.entries(
                          project.floorplans.reduce((acc, plan) => {
                            const bhk = plan.bhkType || 'BHK';
                            if (!acc[bhk]) acc[bhk] = [];
                            acc[bhk].push(plan);
                            return acc;
                          }, {})
                        ).map(([bhkType, plans], idx, arr) => {
                          const areas = plans.map(p => Number(p.area)).filter(Boolean);
                          const prices = plans.map(p => Number(p.price)).filter(Boolean);
                          const minArea = Math.min(...areas);
                          const maxArea = Math.max(...areas);
                          const minPrice = Math.min(...prices);
                          const maxPrice = Math.max(...prices);
                          return (
                            <Box key={bhkType} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', py: 0.5, color: '#bcbcbc', borderBottom: idx === arr.length - 1 ? 'none' : '1px solid #eee' }}>
                              <Box sx={{ minWidth: 70, fontWeight: 500 }}><Typography variant="body2">{bhkType}</Typography></Box>
                              <Box sx={{ minWidth: 120 }}><Typography variant="body2">{minArea === maxArea ? `${minArea} sqft` : `${minArea}-${maxArea} sqft`}</Typography></Box>
                              <Box sx={{ minWidth: 140 }}><Typography variant="body2">{minPrice === maxPrice ? formatPrice(minPrice) : `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`}</Typography></Box>
                            </Box>
                          );
                        })
                      ) : null}
                    </Box>
                    {/* <Box sx={{ display: 'flex', flexDirection: isSmallScreen ? 'column' : 'row', alignItems: isSmallScreen ? 'flex-start' : 'center', mb: 2, gap: 1 }}>
                      <Typography variant="body2">Exciting Offers:</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'Lato, Arial, sans-serif', fontWeight: 600 }} color="error">
                        <span style={{ fontFamily: 'Lato, Arial, sans-serif' }}>₹</span>{formatNumber(project.offerPrice)} for {project.offerMonths} Months
                      </Typography>
                      {project.moreOptions && <Typography variant="body2" sx={{ textDecoration: 'underline', cursor: 'pointer' }}>View 2 More Options</Typography>}
                    </Box> */}

                    <Box sx={{ display: 'flex', flexDirection: isSmallScreen ? 'column' : 'row', alignItems: isSmallScreen ? 'flex-start' : 'center', mb: 2, gap: 1 }}>
                      {(Number(project.offerPrice) > 0) && (
                        <>
                          <Typography variant="body2">Exciting Offers:</Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'Lato, Arial, sans-serif', fontWeight: 600 }} color="error">
                            <span style={{ fontFamily: 'Lato, Arial, sans-serif' }}>₹</span>{formatNumber(project.offerPrice)} for {project.offerMonths} Months
                          </Typography>
                          {project.moreOptions && (
                            <Typography variant="body2" sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                              View 2 More Options
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                    <Stack direction={isSmallScreen ? 'column' : 'row'} spacing={2}>
                      <Button
                        variant="contained"
                        color="error"
                        sx={{ px: 4, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: 10, sm: 10, md: 12, lg: 14 } }}
                        onClick={() => {
                          try {
                            // Store the property ID in sessionStorage
                            sessionStorage.setItem('propertyId', project.id);
                            
                            // Open property details page in new tab without ID in URL
                            window.open('/propertydetails', '_blank');
                          } catch (error) {
                            // Fallback: try direct navigation
                            window.open('/propertydetails', '_blank');
                          }
                        }}
                      >
                        <img src={eye} alt="eye" /> View Details
                      </Button>
                      <Button href="tel:+919705401575" variant="outlined" color="error" sx={{ px: 4 }}>Get Callback</Button>
                    </Stack>
                  </Box>
                  {!isSmallScreen && (
                    <Box sx={{ pr: 3, pt: 3, minWidth: 120 }}>
                      <Typography variant="h4" sx={{ color: red[700], fontWeight: 700 }}>
                        {formatPrice(Number(project.minPrice))}-{formatPrice(Number(project.maxPrice))}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {project.groupName}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Box>
      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
          <Stack spacing={2}>
            <Pagination
              count={pagination.totalPages}
              page={pagination.currentPage}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: 'text.primary',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                },
              }}
            />
            <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
              Page {pagination.currentPage} of {pagination.totalPages} • {pagination.totalItems} total items
            </Box>
          </Stack>
        </Box>
      )}
    </Container>
  );
};

export default ResidentialsList;
